// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Ask Konvo. One palette on Cmd/Ctrl+K: type and every source answers
// (messages, mail, tasks, calendar, plus the channels and people the old quick
// switcher knew), or hand the same words to the Assistant. Keyword results come
// from GET /konvo/search; the "ask" row sends the query to the Assistant DM.

import {Routes} from '@app/app/Routes';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import Guilds from '@app/features/guild/state/Guilds';
import {formatReminderTime, type PaletteAction, parsePaletteAction} from '@app/features/konvo_ask/utils/PaletteActions';
import {normalizePersonName, personMatchScore, rankPalettePeople} from '@app/features/konvo_ask/utils/PaletteSearch';
import {parseKonvoItemLink} from '@app/features/konvo_assistant/utils/KonvoItemLinks';
import EngagementAccess from '@app/features/konvo_engagement/state/EngagementAccess';
import KonvoMail from '@app/features/konvo_mail/state/KonvoMail';
import KonvoReminders from '@app/features/konvo_reminders/state/KonvoReminders';
import {openKonvoCreateTask, openKonvoTask} from '@app/features/konvo_tasks/commands/KonvoTaskCommands';
import KonvoTasks from '@app/features/konvo_tasks/state/KonvoTasks';
import TeamsMeetings from '@app/features/konvo_teams_meetings/state/TeamsMeetings';
import KonvoWhatsApp from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';
import GuildMembers from '@app/features/member/state/GuildMembers';
import MemberSearch, {type SearchContext} from '@app/features/member/state/MemberSearch';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {CloudUpload} from '@app/features/messaging/upload/CloudUpload';
import {goToMessage} from '@app/features/messaging/utils/MessageNavigator';
import SelectedGuild from '@app/features/navigation/state/SelectedGuild';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {loadLazyModule} from '@app/features/platform/utils/LazyModuleLoader';
import * as QuickSwitcherCommands from '@app/features/search/commands/QuickSwitcherCommands';
import {buildCandidateSets} from '@app/features/search/state/QuickSwitcherCandidateBuilder';
import {candidateToResult} from '@app/features/search/state/QuickSwitcherResultConverters';
import {generateGeneralResults} from '@app/features/search/state/QuickSwitcherResultGenerators';
import type {QuickSwitcherExecutableResult} from '@app/features/search/state/QuickSwitcherTypes';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import Users from '@app/features/user/state/Users';
import {FLUXERBOT_ID} from '@fluxer/constants/src/AppConstants';
import {QuickSwitcherResultTypes} from '@fluxer/constants/src/QuickSwitcherConstants';
import {
	ASSISTANT_RESPONSE_PROVIDER,
	type AssistantResponse,
	decodeAssistantResponseFields,
} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import {
	type KonvoSearchFilter,
	type KonvoSearchSource,
	type KonvoSearchSourceState,
	type KonvoSearchSourceStates,
	konvoSearchSources,
	parseKonvoSearchQuery,
} from '@fluxer/schema/src/domains/konvo/KonvoSearch';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import type {I18n} from '@lingui/core';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

export const ASK_KONVO_MODAL_KEY = 'konvo_ask';
const DEBOUNCE_MS = 220;
const MIN_QUERY = 2;
const JUMP_LIMIT = 4;

export interface AskMessageHit {
	id: string;
	channel_id: string;
	guild_id: string | null;
	channel_name: string | null;
	author_id: string;
	author_name: string;
	author_avatar: string | null;
	content: string;
	timestamp: string;
	via_teams: boolean;
}
export interface AskMailHit {
	id: string;
	subject: string;
	from_name: string | null;
	from_address: string | null;
	received_at: string | null;
	is_read: boolean;
	preview: string;
	web_link: string | null;
}
export interface AskTaskHit {
	id: string;
	name: string;
	status: string | null;
	status_color: string | null;
	list: string | null;
	due_date: number | null;
	url: string | null;
	assigned_to_me: boolean;
}
export interface AskEventHit {
	id: string;
	subject: string;
	start: string | null;
	end: string | null;
	location: string | null;
	organizer: string | null;
	join_url: string | null;
	web_link: string | null;
}
export interface AskTeamsHit {
	id: string;
	chat_id: string | null;
	from_name: string | null;
	created_at: string | null;
	preview: string;
	web_link: string | null;
}
export interface AskWhatsAppHit {
	jid: string;
	chat_name: string;
	is_group: boolean;
	message_id: string;
	from_me: boolean;
	sender_name: string | null;
	text: string;
	media_kind: string | null;
	sent_at: string;
}
export interface AskFileHit {
	key: string;
	origin: 'mail' | 'whatsapp' | 'konvo' | 'drive';
	name: string;
	mime: string;
	size: number;
	at: string | null;
	from_name: string | null;
	from_me: boolean;
	/** Mail subject, chat name, #channel or "OneDrive › Folder". */
	where: string;
	/** The matching passage inside the file, when the name itself does not match. */
	snippet: string | null;
	file_id: string | null;
	mail?: {message_id: string; attachment_id: string; conversation_id: string | null; web_link: string | null};
	whatsapp?: {jid: string; message_id: string};
	konvo?: {channel_id: string; guild_id: string | null; message_id: string; attachment_id: string; url: string | null};
	drive?: {drive_id: string; item_id: string; web_url: string | null};
}
/** The type chips of the Files filter. */
export type AskFileType = 'all' | 'pdf' | 'word' | 'excel' | 'powerpoint' | 'pictures';
export type AskRelatedHit =
	| {kind: 'mail'; hit: AskMailHit}
	| {kind: 'task'; hit: AskTaskHit}
	| {kind: 'event'; hit: AskEventHit}
	| {kind: 'teams'; hit: AskTeamsHit}
	| {kind: 'whatsapp'; hit: AskWhatsAppHit};
export interface AskSearchResponse {
	query: string;
	sources?: KonvoSearchSourceStates;
	messages: {hits: Array<AskMessageHit>; total: number; indexing: boolean};
	mail: {hits: Array<AskMailHit>; connected: boolean};
	/** Connected Teams chats are searched alongside the other sources. */
	teams?: {hits: Array<AskTeamsHit>; connected: boolean};
	/** Connected WhatsApp history is searched alongside the other sources. */
	whatsapp?: {hits: Array<AskWhatsAppHit>; connected?: boolean};
	tasks: {hits: Array<AskTaskHit>; connected: boolean; total: number};
	calendar: {hits: Array<AskEventHit>; connected: boolean};
	/** Mail attachments, WhatsApp documents, Konvo chat files and OneDrive, by name and by text inside. */
	files?: {hits: Array<AskFileHit>; connected: boolean; drive_needs_consent?: boolean};
	/** Meaning-based matches the keyword groups did not already list. */
	related?: {hits: Array<AskRelatedHit>};
}

export type AskSource = KonvoSearchSource | 'people' | 'jump';

export interface AskContact {
	source: 'whatsapp' | 'mail';
	id: string;
	name: string;
	detail: string;
	isGroup?: boolean;
}

export type AskItem =
	| {kind: 'contact'; contact: AskContact}
	| {kind: 'action'; action: PaletteAction; title: string; subtitle: string}
	| {kind: 'ask'; query: string}
	| {kind: 'header'; source: AskSource; label: string; count: number}
	| {kind: 'jump'; result: QuickSwitcherExecutableResult}
	| {kind: 'message'; hit: AskMessageHit}
	| {kind: 'mail'; hit: AskMailHit}
	| {kind: 'teams'; hit: AskTeamsHit}
	| {kind: 'whatsapp'; hit: AskWhatsAppHit}
	| {kind: 'task'; hit: AskTaskHit}
	| {kind: 'event'; hit: AskEventHit}
	| {kind: 'file'; hit: AskFileHit};

export type AskSourceKind = 'message' | 'mail' | 'task' | 'event' | 'teams' | 'link';
export interface AskAnswerSource {
	kind: AskSourceKind;
	label: string;
	href: string;
}
export interface AskTurnAttachment {
	url?: string;
	name: string;
	size: number;
}
export interface AskTurn {
	id?: string;
	role: 'user' | 'assistant';
	text: string;
	sources: Array<AskAnswerSource>;
	/** Files the user sent with this turn (user turns only). */
	attachments?: Array<AskTurnAttachment>;
	/** Structured Assistant review card carried by this message, when present. */
	actionCardId?: string;
	/** Native visual blocks carried by an Assistant response embed. */
	structuredResponse?: AssistantResponse;
}
export type AskAnswerStatus = 'idle' | 'sending' | 'waiting' | 'done' | 'failed';

const ANSWER_TIMEOUT_MS = 120_000;
const TASK_LIST_KEY = 'konvo_ask.task_list';

function rememberedListId(): string | null {
	try {
		return getProtectedLocalStorage()?.getItem(TASK_LIST_KEY) ?? null;
	} catch {
		return null;
	}
}

function rememberListId(id: string): void {
	try {
		getProtectedLocalStorage()?.setItem(TASK_LIST_KEY, id);
	} catch {
		// fine
	}
}

/** Pull the links out of an assistant reply so the palette can show them as chips. */
export function extractSources(text: string): Array<AskAnswerSource> {
	const out: Array<AskAnswerSource> = [];
	const seen = new Set<string>();
	const re = /(konvo:\/\/[^\s)>\]]+|https?:\/\/[^\s)>\]]+)/g;
	for (const match of text.matchAll(re)) {
		const href = match[1].replace(/[.,;:!?]+$/, '');
		if (seen.has(href)) continue;
		seen.add(href);
		let kind: AskSourceKind = 'link';
		let label = href;
		const item = parseKonvoItemLink(href);
		if (item) {
			kind = item.kind === 'event' ? 'event' : 'mail';
			label = item.kind === 'event' ? 'Event' : item.kind === 'mail_draft' ? 'Draft' : 'Mail';
		} else if (href.startsWith('konvo://')) {
			kind = 'message';
			label = 'Message';
		} else if (/outlook\.(office|live)\.com|office\.com\/mail/i.test(href)) {
			kind = 'mail';
			label = 'Mail';
		} else if (/clickup\.com/i.test(href)) {
			kind = 'task';
			label = 'Task';
		} else if (/teams\.microsoft\.com/i.test(href)) {
			kind = 'teams';
			label = 'Teams';
		} else {
			try {
				label = new URL(href).hostname.replace(/^www\./, '');
			} catch {
				label = href;
			}
		}
		out.push({kind, label, href});
	}
	return out;
}

/** The reply with its bare links removed, since they show as chips; `shown` limits it to the links that do. */
export function stripSourceLinks(text: string, shown: (href: string) => boolean = () => true): string {
	return text
		.replace(/\(?(konvo:\/\/[^\s)>\]]+|https?:\/\/[^\s)>\]]+)\)?/g, (match, href: string) =>
			shown(href.replace(/[.,;:!?]+$/, '')) ? '' : match,
		)
		.replace(/\[([^\]]+)\]\(\s*\)/g, '$1')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

export function isSelectable(item: AskItem): boolean {
	return item.kind !== 'header';
}

export function askItemKey(item: AskItem): string {
	switch (item.kind) {
		case 'contact':
			return `contact:${item.contact.source}:${item.contact.id}`;
		case 'header':
			return `header:${item.source}`;
		case 'action':
			return `action:${item.action.kind}`;
		case 'ask':
			return 'ask';
		case 'jump':
			return `jump:${item.result.type}:${item.result.id}`;
		case 'whatsapp':
			return `whatsapp:${item.hit.jid}/${item.hit.message_id}`;
		case 'teams':
			return `teams:${item.hit.chat_id ?? ''}/${item.hit.id}`;
		case 'file':
			return `file:${item.hit.key}`;
		default:
			return `${item.kind}:${item.hit.id}`;
	}
}

/** The area the palette was opened over, for the panel's tracking. */
function askPage(): string {
	const path = window.location.pathname;
	if (path.startsWith('/emails') || path.includes('/mail')) return 'mail';
	if (path.startsWith('/tasks')) return 'tasks';
	if (path.startsWith('/calendar')) return 'calendar';
	if (path.startsWith('/today')) return 'home';
	if (path.includes('/whatsapp')) return 'whatsapp';
	if (path.startsWith('/channels')) return 'chats';
	return 'other';
}

/** Which type chip a file falls under; null for files no chip names. */
export function askFileType(hit: Pick<AskFileHit, 'name' | 'mime'>): AskFileType | null {
	const name = hit.name.toLowerCase();
	const mime = hit.mime.toLowerCase();
	if (name.endsWith('.pdf') || mime === 'application/pdf') return 'pdf';
	if (/\.(docx?|docm|dotx?)$/.test(name) || mime.includes('wordprocessingml') || mime === 'application/msword')
		return 'word';
	if (/\.(xlsx?|xlsm|xlsb|csv)$/.test(name) || mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel')
		return 'excel';
	if (/\.(pptx?|ppsx?|pptm)$/.test(name) || mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint')
		return 'powerpoint';
	if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|bmp|tiff?)$/.test(name)) return 'pictures';
	return null;
}

/** Who a file is from, as the From picker lists it; "You" for the user's own. */
export function askFileSender(hit: Pick<AskFileHit, 'from_me' | 'from_name'>): string | null {
	return hit.from_me ? 'You' : hit.from_name;
}

function messageRoute(hit: AskMessageHit): string {
	return hit.guild_id
		? Routes.channelMessage(hit.guild_id, hit.channel_id, hit.id)
		: Routes.dmChannelMessage(hit.channel_id, hit.id);
}

type ModalModule = typeof import('@app/features/konvo_ask/components/AskKonvoModal');

export class AskKonvoStore {
	private disposed = false;
	isOpen = false;
	query = '';
	loading = false;
	error = false;
	sourceFilter: KonvoSearchFilter = 'all';
	/** The Files filter's type chip and From picker; they only narrow what the search returned. */
	fileType: AskFileType = 'all';
	fileFrom: string | null = null;
	sourceStates: Partial<Record<KonvoSearchSource, KonvoSearchSourceState | 'loading'>> = {};
	results: AskSearchResponse | null = null;
	contacts: Array<AskContact> = [];
	contactsLoading = false;
	private selectionIndex = 0;
	private selectedKey: string | null = null;
	mode: 'search' | 'answer' = 'search';
	answerStatus: AskAnswerStatus = 'idle';
	/** Why the last answer failed: the question never got out (`send`), or no reply came in time. */
	answerFailure: 'send' | 'timeout' | null = null;
	/** The question that could not be sent, for Try again. */
	failedQuestion: string | null = null;
	private inlineThread: Array<AskTurn> = [];
	private sharedHistory = false;
	private dmChannelId: string | null = null;
	private stopWatching: (() => void) | null = null;
	private answerTimer: ReturnType<typeof setTimeout> | null = null;
	private i18n: I18n | null = null;
	private requestSeq = 0;
	private abortController: AbortController | null = null;
	private memberSearch: SearchContext | null = null;
	private selectionTouched = false;
	private directoryVersion = 0;
	private debounce: ReturnType<typeof setTimeout> | null = null;
	private modalPromise: Promise<ModalModule> | null = null;
	private readonly logger = new Logger('AskKonvo');

	constructor(options: {sharedHistory?: boolean} = {}) {
		this.sharedHistory = options.sharedHistory === true;
		makeAutoObservable<
			this,
			| 'i18n'
			| 'requestSeq'
			| 'debounce'
			| 'abortController'
			| 'memberSearch'
			| 'modalPromise'
			| 'logger'
			| 'stopWatching'
			| 'answerTimer'
		>(
			this,
			{
				i18n: false,
				requestSeq: false,
				debounce: false,
				abortController: false,
				memberSearch: false,
				modalPromise: false,
				logger: false,
				stopWatching: false,
				answerTimer: false,
			},
			{autoBind: true},
		);
	}

	attachI18n(i18n: I18n): void {
		this.i18n = i18n;
	}

	get trimmedQuery(): string {
		return this.query.trim();
	}

	get searchQuery(): string {
		return parseKonvoSearchQuery(this.query).query;
	}
	get activeSource(): KonvoSearchFilter {
		return (
			parseKonvoSearchQuery(this.query).source ?? (this.trimmedQuery.startsWith('@') ? 'people' : this.sourceFilter)
		);
	}
	get sourceProblems(): Array<{source: KonvoSearchSource; state: KonvoSearchSourceState}> {
		return Object.entries(this.sourceStates)
			.filter(
				([_source, state]) =>
					state === 'error' ||
					state === 'timeout' ||
					state === 'indexing' ||
					(state === 'disconnected' && this.activeSource !== 'all'),
			)
			.map(([source, state]) => ({source: source as KonvoSearchSource, state: state as KonvoSearchSourceState}));
	}

	/** Everything the palette lists, in order, with headers between groups. */
	get items(): Array<AskItem> {
		const query = this.trimmedQuery;
		const items: Array<AskItem> = [];
		const action = query.length >= MIN_QUERY ? parsePaletteAction(query) : null;
		if (action) items.push(this.actionItem(action));
		const jump = this.jumpResults(this.searchQuery);
		const people = jump.filter((result) => result.type === QuickSwitcherResultTypes.USER);
		const contacts = this.contactResults;
		const externalPeople = contacts.filter((contact) => !contact.isGroup);
		const externalChats = contacts.filter((contact) => contact.isGroup);
		const navigation = jump.filter((result) => result.type !== QuickSwitcherResultTypes.USER);
		if (people.length || externalPeople.length) {
			items.push({kind: 'header', source: 'people', label: 'People', count: people.length + externalPeople.length});
			for (const result of people) items.push({kind: 'jump', result});
			for (const contact of externalPeople) items.push({kind: 'contact', contact});
		}
		if (navigation.length + externalChats.length > 0) {
			items.push({kind: 'header', source: 'jump', label: 'Jump to', count: navigation.length + externalChats.length});
			for (const result of navigation) items.push({kind: 'jump', result});
			for (const contact of externalChats) items.push({kind: 'contact', contact});
		}
		const r = this.results;
		if (r && r.query === query) {
			const files = this.fileHits;
			if (files.length > 0) {
				items.push({kind: 'header', source: 'files', label: 'Files', count: files.length});
				for (const hit of files) items.push({kind: 'file', hit});
			}
			if (r.messages.hits.length > 0) {
				items.push({kind: 'header', source: 'messages', label: 'Messages', count: r.messages.total});
				for (const hit of r.messages.hits) items.push({kind: 'message', hit});
			}
			if (r.mail.hits.length > 0) {
				items.push({kind: 'header', source: 'mail', label: 'Mail', count: r.mail.hits.length});
				for (const hit of r.mail.hits) items.push({kind: 'mail', hit});
			}
			if (r.teams && r.teams.hits.length > 0) {
				items.push({kind: 'header', source: 'teams', label: 'Teams', count: r.teams.hits.length});
				for (const hit of r.teams.hits) items.push({kind: 'teams', hit});
			}
			if (r.whatsapp && r.whatsapp.hits.length > 0) {
				items.push({kind: 'header', source: 'whatsapp', label: 'WhatsApp', count: r.whatsapp.hits.length});
				for (const hit of r.whatsapp.hits) items.push({kind: 'whatsapp', hit});
			}
			if (r.tasks.hits.length > 0) {
				items.push({kind: 'header', source: 'tasks', label: 'Tasks', count: r.tasks.total});
				for (const hit of r.tasks.hits) items.push({kind: 'task', hit});
			}
			if (r.calendar.hits.length > 0) {
				items.push({kind: 'header', source: 'calendar', label: 'Calendar', count: r.calendar.hits.length});
				for (const hit of r.calendar.hits) items.push({kind: 'event', hit});
			}
			const related = r.related?.hits ?? [];
			const seen = new Set(items.filter(isSelectable).map(askItemKey));
			const fresh = related.filter((entry) => !seen.has(askItemKey(entry)));
			if (fresh.length > 0) {
				items.push({kind: 'header', source: 'related', label: 'Related by meaning', count: fresh.length});
				for (const entry of fresh) items.push(entry);
			}
		}
		if (query.length >= MIN_QUERY) items.push({kind: 'ask', query});
		return items;
	}

	/** File hits, narrowed by the Files filter's type chip and From picker when that filter is on. */
	get fileHits(): Array<AskFileHit> {
		const r = this.results;
		const hits = r && r.query === this.trimmedQuery ? (r.files?.hits ?? []) : [];
		if (this.activeSource !== 'files') return hits;
		return hits.filter(
			(hit) =>
				(this.fileType === 'all' || askFileType(hit) === this.fileType) &&
				(this.fileFrom == null || askFileSender(hit) === this.fileFrom),
		);
	}

	/** Everyone the found files are from, for the From picker. */
	get fileSenders(): Array<string> {
		const r = this.results;
		const hits = r && r.query === this.trimmedQuery ? (r.files?.hits ?? []) : [];
		return [...new Set(hits.map(askFileSender).filter((name): name is string => Boolean(name)))].sort((a, b) =>
			a === 'You' ? -1 : b === 'You' ? 1 : a.localeCompare(b),
		);
	}

	/** Microsoft is connected without file access yet, so OneDrive was not searched. */
	get driveNeedsConsent(): boolean {
		const r = this.results;
		return r != null && r.query === this.trimmedQuery && r.files?.drive_needs_consent === true;
	}

	setFileType(type: AskFileType): void {
		this.fileType = type;
		this.selectionTouched = false;
		this.selectedIndex = this.firstSelectable();
	}

	setFileFrom(name: string | null): void {
		this.fileFrom = name;
		this.selectionTouched = false;
		this.selectedIndex = this.firstSelectable();
	}

	private get defaultListId(): string | null {
		const lists = KonvoClickUp.lists as ReadonlyArray<{id: string; name: string}>;
		const remembered = rememberedListId();
		if (remembered && lists.some((list) => list.id === remembered)) return remembered;
		return lists[0]?.id ?? null;
	}

	private actionItem(action: PaletteAction): AskItem {
		if (action.kind === 'task') {
			const lists = KonvoClickUp.lists as ReadonlyArray<{id: string; name: string}>;
			const list = lists.find((entry) => entry.id === this.defaultListId) ?? null;
			return {
				kind: 'action',
				action,
				title: `Create task “${action.name}”`,
				subtitle: list ? `in ${list.name} · ⌘↵ picks another list` : 'Enter picks a list',
			};
		}
		return {
			kind: 'action',
			action,
			title: `Remind me ${formatReminderTime(action.at)}`,
			subtitle: action.note,
		};
	}

	/** Run a palette action. `alt` opens the fuller UI instead of doing it straight away. */
	private async runAction(action: PaletteAction, alt: boolean): Promise<void> {
		if (action.kind === 'task') {
			const listId = this.defaultListId;
			if (alt || !listId) {
				this.hide();
				openKonvoCreateTask(action.name);
				return;
			}
			try {
				await http.post(Endpoints.KONVO_CLICKUP_TASKS, {body: {list_id: listId, name: action.name}});
				rememberListId(listId);
				ToastCommands.createToast({type: 'success', children: `Task created: ${action.name}`});
				void KonvoTasks.refresh();
				this.hide();
			} catch (error) {
				this.logger.warn('Task create failed', error);
				ToastCommands.createToast({type: 'error', children: 'Could not create the task in ClickUp.'});
			}
			return;
		}
		try {
			await KonvoReminders.create({remindAt: action.at, note: action.note});
			ToastCommands.createToast({type: 'success', children: `Reminder set for ${formatReminderTime(action.at)}`});
			this.hide();
		} catch (error) {
			this.logger.warn('Reminder create failed', error);
			ToastCommands.createToast({type: 'error', children: 'Could not set the reminder.'});
		}
	}

	/** Alt+Enter on a file: go to the mail, chat or OneDrive page it came from. */
	async confirmSource(): Promise<void> {
		const item = this.selectedItem;
		if (item?.kind !== 'file') return;
		await this.openFileSource(item.hit);
	}

	async openFileSource(hit: AskFileHit): Promise<void> {
		this.hide();
		const {openFileSource} = await import('@app/features/konvo_ask/utils/AskFileOpen');
		openFileSource(hit);
	}

	/** Cmd/Ctrl+Enter: ask Konvo, or the fuller form of a selected action. */
	async confirmAlt(): Promise<void> {
		const item = this.selectedItem;
		if (item?.kind === 'action') {
			await this.runAction(item.action, true);
			return;
		}
		await this.ask();
	}

	get hasRemoteResults(): boolean {
		const r = this.results;
		if (!r || r.query !== this.trimmedQuery) return false;
		return (
			r.messages.hits.length +
				r.mail.hits.length +
				(r.teams?.hits.length ?? 0) +
				(r.whatsapp?.hits.length ?? 0) +
				r.tasks.hits.length +
				r.calendar.hits.length +
				(r.files?.hits.length ?? 0) +
				(r.related?.hits.length ?? 0) >
			0
		);
	}

	get selectedItem(): AskItem | null {
		return this.items[this.selectedIndex] ?? null;
	}
	get selectedIndex(): number {
		if (this.selectionTouched && this.selectedKey) {
			const index = this.items.findIndex((item) => askItemKey(item) === this.selectedKey);
			if (index >= 0) return index;
		}
		return this.selectionIndex;
	}
	set selectedIndex(index: number) {
		this.selectionIndex = index;
		this.selectedKey = this.selectionTouched && this.items[index] ? askItemKey(this.items[index]) : null;
	}

	private jumpResults(query: string): Array<QuickSwitcherExecutableResult> {
		if (!this.i18n || query.length === 0 || (this.activeSource !== 'all' && this.activeSource !== 'people')) return [];
		try {
			void this.directoryVersion;
			// Reading the stores here keeps newly fetched members and renamed users reactive.
			const sets = buildCandidateSets(this.i18n);
			for (const candidate of sets.users) {
				for (const guild of Guilds.getGuilds()) {
					const nick = GuildMembers.getMember(guild.id, candidate.id)?.nick;
					if (nick) candidate.searchValues = [...candidate.searchValues, nick];
				}
			}
			const people = rankPalettePeople(sets.users, query, this.activeSource === 'people' ? 20 : 6).map((candidate) =>
				candidateToResult(candidate, this.i18n!),
			);
			if (this.activeSource === 'people') return people;
			const navigation = generateGeneralResults(query, {...sets, users: []}, this.i18n)
				.filter((result): result is QuickSwitcherExecutableResult => result.type !== QuickSwitcherResultTypes.HEADER)
				.slice(0, JUMP_LIMIT);
			return [...people, ...navigation];
		} catch (error) {
			this.logger.warn('Jump-to results failed', error);
			return [];
		}
	}

	private get contactResults(): Array<AskContact> {
		const source = this.activeSource;
		if (!['all', 'people', 'whatsapp', 'mail'].includes(source) || this.searchQuery.length < MIN_QUERY) return [];
		const seen = new Set<string>();
		return this.contacts
			.filter((contact) => (source === 'all' || source === 'people' ? true : contact.source === source))
			.filter((contact) => source !== 'people' || !contact.isGroup)
			.map((contact) => ({contact, score: personMatchScore(this.searchQuery, [contact.name, contact.detail])}))
			.filter(({contact, score}) => {
				const key = `${contact.source}:${contact.id.toLowerCase()}`;
				if (!score || seen.has(key)) return false;
				seen.add(key);
				return true;
			})
			.sort((a, b) => b.score - a.score || a.contact.name.localeCompare(b.contact.name))
			.slice(0, source === 'all' ? 10 : 25)
			.map(({contact}) => contact);
	}

	private async searchContacts(seq: number): Promise<void> {
		const source = this.activeSource;
		if (!['all', 'people', 'whatsapp', 'mail'].includes(source)) return;
		this.contactsLoading = true;
		const userId = Users.currentUserId;
		const accept = (contacts: Array<AskContact>) => {
			if (seq !== this.requestSeq || this.disposed || userId !== Users.currentUserId) return;
			runInAction(() => {
				this.contacts.push(...contacts);
				if (!this.selectionTouched) this.selectedIndex = this.firstSelectable();
			});
		};
		const pending: Array<Promise<unknown>> = [];
		if (source !== 'whatsapp') {
			pending.push(
				KonvoMail.searchContacts(this.searchQuery.replace(/^@/, '')).then((contacts) =>
					accept(
						contacts.map((contact) => ({
							source: 'mail',
							id: contact.address,
							name: contact.name || contact.address,
							detail: contact.address,
						})),
					),
				),
			);
		}
		if (source !== 'mail') {
			pending.push(
				(async () => {
					await KonvoWhatsApp.ensureStatus();
					if (!KonvoWhatsApp.linked || seq !== this.requestSeq || userId !== Users.currentUserId) return;
					await Promise.allSettled([
						KonvoWhatsApp.refreshChats().then(() =>
							accept(
								KonvoWhatsApp.chats.map((chat) => ({
									source: 'whatsapp',
									id: chat.jid,
									name: chat.name,
									detail: chat.jid.split('@')[0],
									isGroup: chat.is_group,
								})),
							),
						),
						KonvoWhatsApp.loadAddressBook().then((contacts) =>
							accept(
								contacts.map((contact) => ({
									source: 'whatsapp',
									id: contact.jid,
									name: contact.name,
									detail: contact.phone,
								})),
							),
						),
					]);
				})(),
			);
		}
		await Promise.allSettled(pending);
		if (seq === this.requestSeq && !this.disposed && userId === Users.currentUserId) {
			runInAction(() => {
				this.contactsLoading = false;
			});
		}
	}

	show(): void {
		if (this.isOpen) return;
		this.isOpen = true;
		this.query = '';
		this.contacts = [];
		this.contactsLoading = false;
		this.results = null;
		this.error = false;
		this.selectedIndex = 0;
		this.sourceFilter = 'all';
		this.fileType = 'all';
		this.fileFrom = null;
		this.sourceStates = {};
		this.loading = false;
		this.mode = 'search';
		this.answerStatus = 'idle';
		this.inlineThread = [];
		KonvoClickUp.prefetch(true);
		void this.pushModal();
	}

	hide(): void {
		if (!this.isOpen) return;
		this.isOpen = false;
		this.contactsLoading = false;
		if (this.debounce) clearTimeout(this.debounce);
		this.debounce = null;
		this.requestSeq += 1;
		this.abortController?.abort();
		this.memberSearch?.destroy();
		this.memberSearch = null;
		this.loading = false;
		this.stopAnswerWatch();
		if (ModalCommands.getTopModalKey() === ASK_KONVO_MODAL_KEY) ModalCommands.pop();
	}

	/** Leave the answer view and go back to typed results. */
	backToSearch(): void {
		this.abortController?.abort();
		this.requestSeq += 1;
		if (this.debounce) clearTimeout(this.debounce);
		this.loading = false;
		this.sourceStates = {};
		this.stopAnswerWatch();
		this.mode = 'search';
		this.answerStatus = 'idle';
		this.inlineThread = [];
		this.query = '';
		this.contacts = [];
		this.contactsLoading = false;
		this.results = null;
	}

	/** Home and the DM render the same message records, including edits and deletions. */
	get thread(): Array<AskTurn> {
		if (!this.sharedHistory) return this.inlineThread;
		void Messages.version;
		if (!this.dmChannelId) return [];
		return (ChannelMessages.get(this.dmChannelId)?.toArray() ?? []).map((message) => {
			const actionEmbed = message.embeds.find((embed) => embed.author?.name === 'Konvo · Action preview');
			const actionCardId = actionEmbed?.fields?.find((field) => field.name === 'card')?.value.trim() || undefined;
			const responseEmbed = message.embeds.find((embed) => embed.provider?.name === ASSISTANT_RESPONSE_PROVIDER);
			const structuredResponse = decodeAssistantResponseFields(responseEmbed?.fields);
			return {
				id: message.id,
				role: message.author.id === FLUXERBOT_ID ? 'assistant' : 'user',
				text: [
					message.content,
					...(actionCardId || structuredResponse
						? []
						: message.embeds.map((embed) => [embed.title, embed.description].filter(Boolean).join('\n'))),
				]
					.filter(Boolean)
					.join('\n\n'),
				sources: [],
				attachments: message.attachments.map((file) => ({
					name: file.filename,
					size: file.size,
					url: file.url ?? undefined,
				})),
				actionCardId,
				...(structuredResponse ? {structuredResponse} : {}),
			};
		});
	}

	get lastAnswer(): AskTurn | null {
		for (let i = this.thread.length - 1; i >= 0; i -= 1) {
			if (this.thread[i].role === 'assistant') return this.thread[i];
		}
		return null;
	}

	get dmRoute(): string | null {
		return this.dmChannelId ? Routes.dmChannel(this.dmChannelId) : null;
	}

	openAssistant(): void {
		const route = this.dmRoute;
		this.hide();
		if (route) RouterUtils.transitionTo(route);
	}

	/** The Assistant DM, scrolled to one of its messages (an earlier question). */
	openAssistantAt(messageId: string): void {
		const channelId = this.dmChannelId;
		this.hide();
		if (channelId) goToMessage(channelId, messageId);
	}

	private stopAnswerWatch(): void {
		this.stopWatching?.();
		this.stopWatching = null;
		if (this.answerTimer) clearTimeout(this.answerTimer);
		this.answerTimer = null;
	}

	/** Independent embedded chat consumers do not open the search modal. */
	/** The Assistant DM this store talks to, once resolved (see ensureChannel). */
	get channelId(): string | null {
		return this.dmChannelId;
	}

	/** Resolve (or create) the Assistant DM up front so files can be staged against it before the first send. */
	async ensureChannel(): Promise<string | null> {
		if (this.dmChannelId) return this.dmChannelId;
		try {
			const channel = await PrivateChannelCommands.ensureDMChannelResponse(FLUXERBOT_ID);
			if (!channel || this.disposed) return null;
			runInAction(() => {
				this.dmChannelId = channel.id;
			});
			if (this.sharedHistory) {
				await MessageCommands.fetchMessages(channel.id, null, null, 30);
			}
			return channel.id;
		} catch (error) {
			this.logger.warn('Assistant channel unavailable', error);
			return null;
		}
	}

	/** The Assistant DM with its recent messages loaded, so "Recent" lists can read them (phone Ask panel). */
	async ensureHistory(): Promise<string | null> {
		const channelId = await this.ensureChannel();
		if (!channelId || this.disposed) return channelId;
		if (!ChannelMessages.get(channelId)?.ready) {
			try {
				await MessageCommands.fetchMessages(channelId, null, null, 30);
			} catch (error) {
				this.logger.warn('Assistant history unavailable', error);
			}
		}
		return channelId;
	}

	/** Files currently staged in the Home box for the Assistant DM. */
	get pendingAttachmentCount(): number {
		return this.dmChannelId ? CloudUpload.getTextareaAttachments(this.dmChannelId).length : 0;
	}

	async sendQuestion(question: string, options: {withAttachments?: boolean} = {}): Promise<void> {
		const withAttachments = options.withAttachments === true;
		if (this.disposed || (!question.trim() && !withAttachments)) return;
		if (this.answerStatus === 'sending' || this.answerStatus === 'waiting') return;
		await this.askInline(question.trim(), withAttachments);
	}

	/** Try again after the question could not be sent. */
	retryFailed(): void {
		const question = this.failedQuestion;
		if (!question || this.answerStatus !== 'failed') return;
		this.answerStatus = 'idle';
		void this.sendQuestion(question);
	}

	dispose(): void {
		this.disposed = true;
		this.stopAnswerWatch();
		if (this.debounce) clearTimeout(this.debounce);
		this.abortController?.abort();
		this.memberSearch?.destroy();
		this.requestSeq += 1;
	}

	/** Send a question to the Assistant DM and mirror the reply into the palette. */
	private async askInline(question: string, withAttachments = false): Promise<void> {
		this.abortController?.abort();
		this.requestSeq += 1;
		if (this.debounce) clearTimeout(this.debounce);
		this.memberSearch?.cancelSearch();
		this.loading = false;
		this.stopAnswerWatch();
		this.mode = 'answer';
		this.answerStatus = 'sending';
		this.answerFailure = null;
		this.failedQuestion = null;
		const staged = withAttachments && this.dmChannelId ? CloudUpload.getTextareaAttachments(this.dmChannelId) : [];
		const attachments = staged.map((attachment) => ({name: attachment.filename, size: attachment.file.size}));
		if (!this.sharedHistory)
			this.inlineThread = [
				...this.thread,
				{role: 'user', text: question, sources: [], ...(attachments.length > 0 ? {attachments} : {})},
			];
		this.query = '';
		this.contacts = [];
		this.contactsLoading = false;
		this.results = null;
		try {
			const channelId = await this.ensureChannel();
			if (this.disposed) return;
			if (!channelId) throw new Error('no assistant channel');
			// The store only keeps incoming messages for channels it has loaded.
			if (!ChannelMessages.get(channelId)?.ready) {
				await MessageCommands.fetchMessages(channelId, null, null, 30);
			}
			if (this.disposed) return;
			const askedAt = Date.now();
			const nonce = SnowflakeUtils.fromTimestamp(askedAt);
			// Claiming moves the staged files from the box onto this nonce; send() then uploads them.
			const claimed =
				staged.length > 0
					? CloudUpload.claimAttachmentsForMessage(channelId, nonce, undefined, {content: question})
					: [];
			runInAction(() => {
				this.answerStatus = 'waiting';
			});
			await MessageCommands.send(channelId, {content: question, nonce, hasAttachments: claimed.length > 0});
			if (!this.disposed) this.watchForReply(channelId, askedAt);
		} catch (error) {
			this.logger.warn('Ask failed', error);
			runInAction(() => {
				this.answerStatus = 'failed';
				this.answerFailure = 'send';
				this.failedQuestion = question;
			});
		}
	}

	private watchForReply(channelId: string, askedAt: number): void {
		this.stopAnswerWatch();
		const seen = new Set<string>();
		const dispose = reaction(
			() => {
				// ChannelMessages instances are replaced on every change; the
				// Messages store's version is what actually ticks.
				void Messages.version;
				return ChannelMessages.get(channelId)?.toArray() ?? [];
			},
			(messages) => {
				const toTurn = (m: (typeof messages)[number]): AskTurn => {
					const text = m.content ?? '';
					const responseEmbed = m.embeds.find((embed) => embed.provider?.name === ASSISTANT_RESPONSE_PROVIDER);
					const structuredResponse = decodeAssistantResponseFields(responseEmbed?.fields);
					return {
						id: m.id,
						role: 'assistant',
						text: stripSourceLinks(text),
						sources: extractSources(text),
						...(structuredResponse ? {structuredResponse} : {}),
					};
				};
				const answers = messages.filter((m) => m.author.id === FLUXERBOT_ID && m.timestamp.getTime() >= askedAt - 2000);
				const replies = answers.filter((m) => !seen.has(m.id));
				// A streamed reply arrives as its first words and grows by edits: keep the inline copy in step.
				const grown = new Map<string, AskTurn>();
				if (!this.sharedHistory) {
					for (const m of answers) {
						if (!seen.has(m.id)) continue;
						const turn = this.inlineThread.find((entry) => entry.id === m.id);
						const next = toTurn(m);
						if (
							turn &&
							(turn.text !== next.text ||
								JSON.stringify(turn.structuredResponse) !== JSON.stringify(next.structuredResponse))
						)
							grown.set(m.id, next);
					}
				}
				if (replies.length === 0 && grown.size === 0) return;
				runInAction(() => {
					if (grown.size > 0) {
						this.inlineThread = this.inlineThread.map((turn) => (turn.id && grown.get(turn.id)) || turn);
					}
					if (replies.length === 0) return;
					for (const m of replies) {
						seen.add(m.id);
						if (!this.sharedHistory) this.inlineThread = [...this.thread, toTurn(m)];
					}
					this.answerStatus = 'done';
				});
				if (this.answerTimer) clearTimeout(this.answerTimer);
				this.answerTimer = null;
			},
			{fireImmediately: true},
		);
		this.stopWatching = dispose;
		this.answerTimer = setTimeout(() => {
			runInAction(() => {
				if (this.answerStatus === 'waiting') {
					this.answerStatus = 'failed';
					this.answerFailure = 'timeout';
				}
			});
		}, ANSWER_TIMEOUT_MS);
	}

	/** Enter in the answer view: a follow-up on the same thread. */
	async followUp(): Promise<void> {
		const question = this.trimmedQuery;
		if (question.length === 0) return;
		await this.askInline(question);
	}

	toggle(): void {
		if (this.isOpen) this.hide();
		else this.show();
	}

	setQuery(query: string): void {
		const seq = ++this.requestSeq;
		this.abortController?.abort();
		this.memberSearch?.cancelSearch();
		this.query = query;
		this.contacts = [];
		this.contactsLoading = false;
		this.results = null;
		this.sourceStates = {};
		this.selectionTouched = false;
		this.selectedIndex = this.firstSelectable();
		this.error = false;
		if (this.debounce) clearTimeout(this.debounce);
		if (this.searchQuery.length < MIN_QUERY) {
			this.results = null;
			this.loading = false;
			return;
		}
		this.loading = true;
		this.debounce = setTimeout(() => {
			void this.fetch(query.trim(), seq);
			this.searchPeople(seq);
			void this.searchContacts(seq);
		}, DEBOUNCE_MS);
	}

	setSourceFilter(source: KonvoSearchFilter): void {
		const parsed = parseKonvoSearchQuery(this.query);
		this.sourceFilter = source;
		this.fileType = 'all';
		this.fileFrom = null;
		this.setQuery(parsed.source ? parsed.query : this.query.replace(/^@/, ''));
	}

	retrySearch(): void {
		this.setQuery(this.query);
	}

	private searchPeople(seq: number): void {
		if (this.activeSource !== 'all' && this.activeSource !== 'people') return;
		const query = this.searchQuery.replace(/^@/, '');
		if (query.split(/\s+/).length > 3 && this.activeSource !== 'people') return;
		this.memberSearch ??= MemberSearch.getSearchContext(() => {
			if (this.disposed || this.searchQuery.length < MIN_QUERY) return;
			runInAction(() => {
				this.directoryVersion += 1;
				if (!this.selectionTouched) this.selectedIndex = this.firstSelectable();
			});
		}, 25);
		this.memberSearch.beginSearch(query);
		const guildIds = Guilds.getGuilds()
			.filter((guild) => !GuildMembers.isGuildFullyLoaded(guild.id))
			.map((guild) => guild.id);
		const prefix = normalizePersonName(query).split(' ')[0];
		if (prefix.length < 2) return;
		void MemberSearch.fetchMembersInBackground(prefix, guildIds, SelectedGuild.selectedGuildId ?? undefined)
			.then(() => {
				if (seq !== this.requestSeq || this.disposed) return;
				runInAction(() => {
					this.directoryVersion += 1;
					if (!this.selectionTouched) this.selectedIndex = this.firstSelectable();
				});
			})
			.catch((error) => this.logger.warn('People search unavailable', error));
	}

	private async fetch(query: string, seq: number): Promise<void> {
		if (seq !== this.requestSeq || this.disposed) return;
		const controller = new AbortController();
		this.abortController = controller;
		const userId = Users.currentUserId;
		const sources = konvoSearchSources(this.activeSource);
		runInAction(() => {
			this.results = {
				query,
				sources: {},
				messages: {hits: [], total: 0, indexing: false},
				mail: {hits: [], connected: false},
				tasks: {hits: [], connected: false, total: 0},
				calendar: {hits: [], connected: false},
				files: {hits: [], connected: false},
				related: {hits: []},
			};
			this.sourceStates = Object.fromEntries(sources.map((source) => [source, 'loading']));
			this.loading = sources.length > 0;
		});
		await Promise.all(
			sources.map(async (source) => {
				try {
					const response = await http.get<AskSearchResponse>(Endpoints.KONVO_SEARCH, {
						query: {
							q: query,
							limit: this.activeSource === 'all' ? 5 : this.activeSource === 'files' ? 25 : 10,
							sources: source,
							...(this.activeSource === 'all' ? {} : {live: '1'}),
						},
						signal: controller.signal,
						timeoutMs: 8000,
						retries: 0,
					});
					if (seq !== this.requestSeq || controller.signal.aborted || userId !== Users.currentUserId) return;
					runInAction(() => {
						const selected = this.selectedItem ? askItemKey(this.selectedItem) : null;
						if (response.body && this.results) this.results = {...this.results, [source]: response.body[source]};
						this.sourceStates[source] = response.body?.sources?.[source] ?? 'ready';
						this.loading = Object.values(this.sourceStates).includes('loading');
						const preserved = selected ? this.items.findIndex((item) => askItemKey(item) === selected) : -1;
						this.selectedIndex = this.selectionTouched && preserved >= 0 ? preserved : this.firstSelectable();
					});
				} catch (error) {
					if (seq !== this.requestSeq || controller.signal.aborted || userId !== Users.currentUserId) return;
					this.logger.warn(`Search source ${source} failed`, error);
					runInAction(() => {
						this.sourceStates[source] = 'error';
						this.loading = Object.values(this.sourceStates).includes('loading');
					});
				}
			}),
		);
	}

	private firstSelectable(): number {
		const index = this.items.findIndex(isSelectable);
		return index < 0 ? 0 : index;
	}

	move(direction: 'up' | 'down'): void {
		this.selectionTouched = true;
		const items = this.items;
		if (items.length === 0) return;
		let index = this.selectedIndex;
		for (let step = 0; step < items.length; step += 1) {
			index = (index + (direction === 'down' ? 1 : -1) + items.length) % items.length;
			if (isSelectable(items[index])) {
				this.selectedIndex = index;
				return;
			}
		}
	}

	select(index: number): void {
		this.selectionTouched = true;
		if (index >= 0 && index < this.items.length && isSelectable(this.items[index])) this.selectedIndex = index;
	}

	async confirm(): Promise<void> {
		const item = this.selectedItem;
		if (!item) return;
		await this.execute(item);
	}

	async ask(): Promise<void> {
		const query = this.trimmedQuery;
		if (query.length < MIN_QUERY) return;
		await this.execute({kind: 'ask', query});
	}

	async execute(item: AskItem): Promise<void> {
		switch (item.kind) {
			case 'header':
				return;
			case 'action':
				await this.runAction(item.action, false);
				return;
			case 'ask':
				// The palette's "Ask Konvo" row (and ⌘Enter) opens the corner assistant's panel; without
				// the new assistant experience (the rollout) it answers here in the palette as before.
				if (EngagementAccess.enabled && this.isOpen && !this.sharedHistory) {
					this.hide();
					// Loaded on demand: the panel pulls in UI strings this store (and its tests) don't need.
					const {default: KonvoPanel} = await import('@app/features/konvo_panel/state/KonvoPanel');
					KonvoPanel.open({starter: 'free', request: item.query, source: 'ask', page: askPage()});
					return;
				}
				await this.askInline(item.query);
				return;
			case 'contact':
				this.hide();
				if (item.contact.source === 'whatsapp') {
					RouterUtils.transitionTo(Routes.dmWhatsAppChat(item.contact.id));
				} else {
					KonvoMail.clearSearch();
					KonvoMail.setQuery(`from:${item.contact.id}`);
					RouterUtils.transitionTo(Routes.EMAILS);
				}
				return;
			case 'jump':
				this.hide();
				await QuickSwitcherCommands.switchTo(item.result);
				return;
			case 'message':
				this.hide();
				RouterUtils.transitionTo(messageRoute(item.hit));
				return;
			case 'mail':
				this.hide();
				KonvoMail.select(item.hit.id);
				RouterUtils.transitionTo(Routes.dmMail(item.hit.id));
				return;
			case 'teams':
				this.hide();
				if (item.hit.web_link) openExternalUrl(item.hit.web_link);
				return;
			case 'whatsapp':
				this.hide();
				RouterUtils.transitionTo(Routes.dmWhatsAppChat(item.hit.jid));
				return;
			case 'task':
				this.hide();
				if (item.hit.id) openKonvoTask(item.hit.id);
				else if (item.hit.url) openExternalUrl(item.hit.url);
				return;
			case 'event':
				this.hide();
				if (item.hit.join_url) void TeamsMeetings.open(item.hit.join_url, item.hit.subject);
				else RouterUtils.transitionTo(Routes.CALENDAR);
				return;
			case 'file': {
				this.hide();
				// Loaded on demand: the preview window is large and most searches never open a file.
				const {openFilePreview} = await import('@app/features/konvo_ask/utils/AskFileOpen');
				openFilePreview(item.hit);
				return;
			}
		}
	}

	private loadModal(): Promise<ModalModule> {
		this.modalPromise ??= loadLazyModule(() => import('@app/features/konvo_ask/components/AskKonvoModal')).catch(
			(error) => {
				this.modalPromise = null;
				throw error;
			},
		);
		return this.modalPromise;
	}

	private async pushModal(): Promise<void> {
		let module: ModalModule;
		try {
			module = await this.loadModal();
		} catch (error) {
			this.logger.error('Ask Konvo modal failed to load', error);
			this.hide();
			return;
		}
		if (!this.isOpen) return;
		const {AskKonvoModal} = module;
		ModalCommands.pushWithKey(
			modal(() => <AskKonvoModal data-flx="konvo-ask.modal" />),
			ASK_KONVO_MODAL_KEY,
		);
	}
}

export default new AskKonvoStore();
